import React, { useMemo } from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";

import { useDataset } from "../terrain3d/datapack";
import { buildChart, formatNumber, getChartSpec } from "../terrain3d/chartSpecs";

ChartJS.register(
  ArcElement, BarElement, CategoryScale, Legend, LineElement, LinearScale, PointElement, Tooltip
);

/**
 * RENDU D'UN GRAPHIQUE DE TERRITOIRE.
 *
 * Un seul composant pour tous les graphiques du catalogue. Les specifications
 * (chartSpecs.js) preparent des nombres dans une forme unique ; ce module les
 * met en image, et lui seul connait Chart.js. C'est ce qui permet d'ajouter un
 * graphique sans toucher au dessin, et de changer le dessin sans relire quatre
 * fonctions de calcul.
 *
 * IL EST PROJETE DANS UNE SALLE. D'ou les partis pris qui suivent, tous
 * orientes par la distance de lecture plutot que par la densite : peu de series
 * a la fois, une grille qui s'efface, des etiquettes lisibles au fond de la
 * salle, et les valeurs ecrites en clair sur les parts d'un camembert — un
 * auditoire ne compare pas des angles.
 */

// Encres du module (cf. TerritoryIntroPage.css). Chart.js dessine dans un
// canvas : il ne voit pas les variables CSS, il faut lui donner des couleurs.
const INK = "#f4f8f9";
const INK_DIM = "rgba(244, 248, 249, 0.68)";
const GRID = "rgba(255, 255, 255, 0.1)";

function formatValue(value, chart) {
  const unit = chart.unit ? ` ${chart.unit}` : "";
  return `${formatNumber(value, chart.decimals ?? 1)}${unit}`;
}

/**
 * Corps de texte des graphiques. Ces tailles sont exprimees dans le repere de
 * l'ecran projete (1920x1080) : ce qui parait genereux dans un navigateur est
 * la taille juste au fond d'une salle. Les valeurs par defaut de Chart.js
 * (12 px) y sont illisibles.
 */
const FONT_TICK = 15;
const FONT_LEGEND = 15;
const FONT_AXIS = 14;

const BASE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 250 },
  // Le survol vise la colonne entiere : pointer un point de 3 px a la souris,
  // devant une salle, ne marche pas.
  interaction: { mode: "index", intersect: false },
};

/**
 * PAS DE VALEUR SUR CHAQUE POINT. Le greffon datalabels est enregistre
 * globalement par la fiche de station du viewer, et Chart.js applique ses
 * greffons a TOUS les graphiques de la page : sans cette extinction explicite,
 * les douze mois de chaque horizon arrivaient couverts de nombres a douze
 * decimales. Ce que ces graphiques montrent, c'est une forme — une saisonnalite,
 * un deplacement entre horizons. Les valeurs exactes sont dans l'infobulle, et
 * dans la legende pour les parts.
 */
const NO_DATALABELS = { display: false };

function legendOptions(chart, panelSize) {
  return {
    // Une serie unique est deja nommee par le titre : une legende d'un seul
    // element est du bruit. Un camembert, lui, tire son identite de ses parts.
    display: chart.form === "pie" || chart.series.length > 1,
    // La legende d'un camembert porte les libelles ET les valeurs : posee a
    // droite, elle prend la moitie d'une colonne de 470 px et ecrase le
    // dessin. Elle passe donc sous le graphique dans le rail.
    position: chart.form === "pie" ? (panelSize === "rail" ? "bottom" : "right") : "top",
    align: chart.form === "pie" ? "center" : "end",
    labels: {
      color: INK,
      boxWidth: 12,
      boxHeight: 12,
      usePointStyle: true,
      pointStyle: "rectRounded",
      padding: 12,
      font: { size: FONT_LEGEND },
    },
  };
}

function tooltipOptions(chart) {
  return {
    backgroundColor: "rgba(10, 26, 36, 0.95)",
    borderColor: "rgba(84, 168, 186, 0.3)",
    borderWidth: 1,
    titleColor: INK,
    bodyColor: INK,
    padding: 12,
    titleFont: { size: FONT_AXIS },
    bodyFont: { size: FONT_AXIS },
    callbacks: {
      // `raw` plutot que `parsed` : sur des barres couchees, `parsed.y` porte
      // l'index de la categorie, pas la valeur. `raw` est la donnee d'origine,
      // quelle que soit l'orientation.
      label: (item) => {
        const name = chart.form === "pie" ? item.label : item.dataset.label;
        return `${name} : ${formatValue(item.raw, chart)}`;
      },
    },
  };
}

function cartesianScales(chart, { horizontal = false } = {}) {
  const category = {
    grid: { display: false },
    border: { color: GRID },
    ticks: { color: INK_DIM, font: { size: FONT_TICK }, maxRotation: 0, autoSkipPadding: 8 },
  };
  const value = {
    // Le zero est garde : sur des cumuls et des debits, tronquer l'axe
    // exagererait des ecarts que l'atelier doit justement estimer.
    beginAtZero: true,
    grid: { color: GRID, drawTicks: false },
    border: { display: false },
    ticks: {
      color: INK_DIM,
      font: { size: FONT_TICK },
      padding: 8,
      // Les graduations n'ont pas besoin des decimales des valeurs : « 90 »
      // plutot que « 90,0 » sur un axe de debits.
      callback: (tick) => formatNumber(tick, 0),
    },
    title: chart.unit ? { display: true, text: chart.unit, color: INK_DIM, font: { size: FONT_AXIS } } : undefined,
  };
  return horizontal ? { x: value, y: category } : { x: category, y: value };
}

function LineChart({ chart, panelSize }) {
  const data = {
    labels: chart.labels,
    datasets: chart.series.map((series) => ({
      label: series.label,
      data: series.values,
      borderColor: series.color,
      backgroundColor: series.color,
      // Trois pixels, pas deux : le trait est vu à dix mètres, à travers un
      // vidéoprojecteur qui mange le contraste des traits fins.
      borderWidth: 3,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHitRadius: 12,
      tension: 0.25,
    })),
  };
  return (
    <Line
      data={data}
      options={{
        ...BASE_OPTIONS,
        plugins: {
          legend: legendOptions(chart, panelSize),
          tooltip: tooltipOptions(chart),
          datalabels: NO_DATALABELS,
        },
        scales: cartesianScales(chart),
      }}
    />
  );
}

function BarChart({ chart, panelSize }) {
  // Une REPARTITION en barres (une couleur par barre) porte des libellés de
  // catégories — « Industrie et activités », « Fourrages et surfaces toujours
  // en herbe ». À la verticale, ils se couchent ou disparaissent. Couchées,
  // les barres leur donnent une ligne entière chacune. Un CYCLE ANNUEL, lui,
  // garde ses douze mois à la verticale : le temps se lit de gauche à droite.
  const horizontal = Boolean(chart.series[0]?.colors);
  const data = {
    labels: chart.labels,
    datasets: chart.series.map((series) => ({
      label: series.label,
      data: series.values,
      // Une repartition en barres porte ses couleurs par barre (une par
      // categorie) ; un cycle annuel les porte par serie (une par horizon).
      // Pleine opacité : trois horizons superposés à 75 % viraient tous au
      // gris-bleu du fond. Ce sont de petites marques, elles peuvent être
      // franches.
      backgroundColor: series.colors || series.color,
      borderColor: series.colors || series.color,
      borderWidth: 0,
      borderRadius: 4,
      // Deux pixels de fond entre deux remplissages voisins : sans cet
      // interstice, des barres adjacentes se lisent comme un seul aplat.
      borderSkipped: false,
      barPercentage: 0.86,
      categoryPercentage: 0.86,
    })),
  };
  return (
    <Bar
      data={data}
      options={{
        ...BASE_OPTIONS,
        indexAxis: horizontal ? "y" : "x",
        plugins: {
          legend: legendOptions(chart, panelSize),
          tooltip: tooltipOptions(chart),
          datalabels: NO_DATALABELS,
        },
        scales: cartesianScales(chart, { horizontal }),
      }}
    />
  );
}

function PieChart({ chart, panelSize }) {
  const series = chart.series[0];
  const total = series.values.reduce((sum, value) => sum + value, 0);
  const data = {
    labels: chart.labels,
    datasets: [{
      data: series.values,
      backgroundColor: series.colors,
      // L'anneau de fond entre deux parts : c'est lui qui tient la lecture
      // quand deux teintes voisines se ressemblent en vision des couleurs
      // deficiente.
      borderColor: "#12293a",
      borderWidth: 2,
      hoverOffset: 6,
    }],
  };
  return (
    <Pie
      data={data}
      options={{
        ...BASE_OPTIONS,
        interaction: { mode: "nearest", intersect: true },
        plugins: {
          datalabels: NO_DATALABELS,
          legend: {
            ...legendOptions(chart, panelSize),
            labels: {
              ...legendOptions(chart, panelSize).labels,
              // La legende porte la valeur : l'identite d'une part ne repose
              // jamais sur sa seule couleur.
              generateLabels: (instance) => {
                const dataset = instance.data.datasets[0];
                return instance.data.labels.map((label, index) => ({
                  text: `${label} : ${formatValue(dataset.data[index], chart)}`,
                  fillStyle: dataset.backgroundColor[index],
                  strokeStyle: dataset.backgroundColor[index],
                  // Chart.js peint le texte de legende avec `fontColor` de
                  // CHAQUE element, pas avec `labels.color` : c'est le
                  // generateur par defaut qui recopie l'un dans l'autre. Le
                  // notre doit le faire aussi, sinon le canvas retombe sur
                  // son noir initial — du noir sur le fond sombre du panneau.
                  fontColor: INK,
                  lineWidth: 0,
                  pointStyle: "rectRounded",
                  index,
                }));
              },
            },
          },
          tooltip: {
            ...tooltipOptions(chart),
            callbacks: {
              label: (item) => {
                const value = formatValue(item.raw, chart);
                // Une part deja exprimee en pourcentage n'a pas besoin qu'on
                // lui recalcule sa part.
                if (chart.unit === "%") return `${item.label} : ${value}`;
                const share = total ? (item.raw / total) * 100 : 0;
                return `${item.label} : ${value} (${formatNumber(share, 0)} %)`;
              },
            },
          },
        },
      }}
    />
  );
}

const FORMS = { line: LineChart, bar: BarChart, pie: PieChart };

/**
 * Un graphique, de sa specification a son cadre. `selection` porte ce qui est
 * ouvert sur la carte ({ discharge, commune, climatePoint }) : les graphiques
 * regles sur « suivre la selection » s'y accrochent.
 */
export default function TerritoryChart({ territoryId, chart, selection, panelSize }) {
  const spec = getChartSpec(chart?.chart_id);
  const { data, loading, error } = useDataset(territoryId, spec?.dataset || null);

  const built = useMemo(() => {
    if (!spec) return { empty: "Choisissez un graphique." };
    if (loading) return null;
    if (error) return { empty: "Les données de ce graphique n'ont pas pu être chargées." };
    return buildChart(spec, data, chart?.params, selection);
  }, [spec, data, loading, error, chart?.params, selection]);

  if (!built) {
    return (
      <figure className="territory-chart">
        <p className="territory-chart-empty">Chargement des données…</p>
      </figure>
    );
  }

  if (built.empty) {
    return (
      <figure className="territory-chart">
        <p className="territory-chart-empty">{built.empty}</p>
      </figure>
    );
  }

  const Form = FORMS[built.form] || LineChart;
  const footnote = [...(built.notes || []), built.source].filter(Boolean);

  return (
    <figure className="territory-chart">
      <figcaption className="territory-chart-head">
        <span className="territory-chart-title">{chart.title || built.title}</span>
        {built.subtitle && <span className="territory-chart-subtitle">{built.subtitle}</span>}
      </figcaption>

      <div className="territory-chart-plot">
        <Form chart={built} panelSize={panelSize} />
      </div>

      {/* Le commentaire de l'auteur — ce qu'il faut retenir du dessin. Il
          vient SOUS le graphique et non sous le titre : deux graphiques côte
          à côte gardent ainsi leurs tracés alignés, quelle que soit la
          longueur de ce qu'on a écrit sous l'un ou l'autre. */}
      {chart.text && (
        <p className="territory-chart-text">{chart.text}</p>
      )}

      {/* Une seule ligne de bas de cadre. Les réserves (l'année sur laquelle
          porte la somme, les ouvrages muets, le secret statistique) et la
          provenance sont du même ordre : ce qu'il faut savoir pour lire le
          chiffre sans le sur-interpréter. En deux paragraphes distincts, elles
          prenaient plus de place que le dessin. */}
      {(footnote.length > 0) && (
        <p className="territory-chart-notes">{footnote.join(" · ")}</p>
      )}
    </figure>
  );
}

/** La grille d'une etape : un a trois graphiques cote a cote. */
export function TerritoryChartGrid({ territoryId, charts, selection, panelSize }) {
  const list = charts || [];
  if (!list.length) {
    return <p className="territory-chart-empty">Aucun graphique n'a été ajouté à cette étape.</p>;
  }
  return (
    <div className={`territory-chart-grid territory-chart-grid--${Math.min(list.length, 3)}`}>
      {list.map((chart, index) => (
        <TerritoryChart
          key={chart.chart_uid || index}
          territoryId={territoryId}
          chart={chart}
          selection={selection}
          panelSize={panelSize}
        />
      ))}
    </div>
  );
}
